import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, RefreshCw, Search, Hash } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { companyAPI } from '../services/api';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const emptyForm = {
  number_range_id: '',
  company_code_id: '',
  fiscal_year: String(new Date().getFullYear()),
  from_number: '',
  to_number: '',
  current_number: '',
  is_external: false,
  description: '',
};

const FINumberRanges = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('fi_number_range');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [rows, setRows] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCC, setFilterCC] = useState('all');
  const [filterFY, setFilterFY] = useState('all');
  const [formData, setFormData] = useState(emptyForm);

  // eslint-disable-next-line
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [nr, cc] = await Promise.all([
        axios.get(`${backendUrl}/api/fi/number-ranges`),
        companyAPI.getCompanyCodes(),
      ]);
      setRows(nr.data || []);
      setCompanyCodes(cc.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load number ranges');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setFormData(emptyForm); setEditMode(false); setDialogOpen(true); };
  const openEdit = (row) => {
    setFormData({
      number_range_id: row.number_range_id,
      company_code_id: row.company_code_id,
      fiscal_year: row.fiscal_year,
      from_number: row.from_number,
      to_number: row.to_number,
      current_number: row.current_number ?? row.from_number,
      is_external: !!row.is_external,
      description: row.description || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.number_range_id || !formData.company_code_id || !formData.fiscal_year) {
      toast.error('ID, Company Code and Fiscal Year are required'); return;
    }
    if (formData.from_number === '' || formData.to_number === '') {
      toast.error('From Number and To Number are required'); return;
    }
    if (Number(formData.from_number) >= Number(formData.to_number)) {
      toast.error('From Number must be less than To Number'); return;
    }
    try {
      const payload = {
        ...formData,
        from_number: Number(formData.from_number),
        to_number: Number(formData.to_number),
        // empty current_number → send null so backend defaults to from_number
        current_number:
          formData.current_number === '' || formData.current_number === null
            ? null
            : Number(formData.current_number),
      };
      if (editMode) {
        const { number_range_id, company_code_id, fiscal_year, ...rest } = payload;
        await axios.put(
          `${backendUrl}/api/fi/number-ranges/${number_range_id}/${company_code_id}/${fiscal_year}`,
          rest
        );
        toast.success('Number range updated');
      } else {
        await axios.post(`${backendUrl}/api/fi/number-ranges`, payload);
        toast.success('Number range created');
      }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete FI Number Range '${row.number_range_id}' for ${row.company_code_id}/${row.fiscal_year}?`)) return;
    try {
      await axios.delete(
        `${backendUrl}/api/fi/number-ranges/${row.number_range_id}/${row.company_code_id}/${row.fiscal_year}`
      );
      toast.success('Deleted'); loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = rows.filter((r) => {
    if (filterCC !== 'all' && r.company_code_id !== filterCC) return false;
    if (filterFY !== 'all' && r.fiscal_year !== filterFY) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.number_range_id?.toLowerCase().includes(q) ||
      r.description?.toLowerCase().includes(q)
    );
  });

  const fiscalYears = Array.from(new Set(rows.map((r) => r.fiscal_year))).sort();

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="fi-number-ranges-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Hash className="w-7 h-7 text-orange-600" />
              FI Number Ranges
            </h1>
            <p className="text-gray-600 mt-1">
              Per company code &amp; fiscal year. Overlap-protected.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} data-testid="refresh-fi-nr-btn">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            {writable && (
            <Button onClick={openCreate} data-testid="new-fi-nr-btn">
              <Plus className="w-4 h-4 mr-1" />New Number Range
            </Button>
            )}
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4 grid grid-cols-3 gap-3">
            <div className="relative col-span-1">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                placeholder="Search…"
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="fi-nr-search-input"
              />
            </div>
            <Select value={filterCC} onValueChange={setFilterCC}>
              <SelectTrigger data-testid="fi-nr-filter-cc"><SelectValue placeholder="Company Code" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Company Codes</SelectItem>
                {companyCodes.map((c) => (
                  <SelectItem key={c.company_code_id} value={c.company_code_id}>
                    {c.company_code_id} - {c.company_code_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterFY} onValueChange={setFilterFY}>
              <SelectTrigger><SelectValue placeholder="Fiscal Year" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fiscal Years</SelectItem>
                {fiscalYears.map((fy) => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{filtered.length} Number Range(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>FY</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Current</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>External</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => {
                    const total = (row.to_number - row.from_number + 1) || 1;
                    const used = Math.max(0, (row.current_number || row.from_number) - row.from_number);
                    const pct = Math.min(100, Math.round((used / total) * 100));
                    const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
                    return (
                    <TableRow
                      key={`${row.number_range_id}-${row.company_code_id}-${row.fiscal_year}`}
                      data-testid={`fi-nr-row-${row.number_range_id}-${row.company_code_id}`}
                    >
                      <TableCell><Badge variant="outline">{row.number_range_id}</Badge></TableCell>
                      <TableCell>{row.company_code_id}</TableCell>
                      <TableCell>{row.fiscal_year}</TableCell>
                      <TableCell>{row.from_number}</TableCell>
                      <TableCell>{row.to_number}</TableCell>
                      <TableCell>{row.current_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[120px]">
                          <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-mono text-gray-700 w-9 text-right">{pct}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{row.is_external ? <Badge>Ext</Badge> : <Badge variant="secondary">Int</Badge>}</TableCell>
                      <TableCell className="text-xs text-gray-600">{row.description}</TableCell>
                      <TableCell className="text-right">
                        <RecordActions
                          doctype="fi_number_range"
                          idForTestId={`fi-nr-${row.number_range_id}-${row.company_code_id}`}
                          onView={() => setViewItem(row)}
                          onEdit={() => openEdit(row)}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="fi-nr-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'New'} FI Number Range</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Number Range ID *</Label>
                <Input
                  value={formData.number_range_id}
                  maxLength={10}
                  disabled={editMode}
                  onChange={(e) => setFormData({ ...formData, number_range_id: e.target.value.toUpperCase() })}
                  data-testid="fi-nr-id-input"
                />
              </div>
              <div>
                <Label>Company Code *</Label>
                <Select
                  value={formData.company_code_id}
                  onValueChange={(v) => setFormData({ ...formData, company_code_id: v })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="fi-nr-cc-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {companyCodes.map((c) => (
                      <SelectItem key={c.company_code_id} value={c.company_code_id}>
                        {c.company_code_id} - {c.company_code_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fiscal Year *</Label>
                <Input
                  value={formData.fiscal_year}
                  maxLength={4}
                  disabled={editMode}
                  onChange={(e) => setFormData({ ...formData, fiscal_year: e.target.value })}
                  data-testid="fi-nr-fy-input"
                />
              </div>
              <div>
                <Label>From Number *</Label>
                <Input
                  type="number"
                  value={formData.from_number}
                  onChange={(e) => setFormData({ ...formData, from_number: e.target.value })}
                  data-testid="fi-nr-from-input"
                />
              </div>
              <div>
                <Label>To Number *</Label>
                <Input
                  type="number"
                  value={formData.to_number}
                  onChange={(e) => setFormData({ ...formData, to_number: e.target.value })}
                  data-testid="fi-nr-to-input"
                />
              </div>
              <div>
                <Label>Current Number</Label>
                <Input
                  type="number"
                  placeholder="defaults to From Number"
                  value={formData.current_number}
                  onChange={(e) => setFormData({ ...formData, current_number: e.target.value })}
                  data-testid="fi-nr-current-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <Checkbox
                  checked={formData.is_external}
                  onCheckedChange={(v) => setFormData({ ...formData, is_external: !!v })}
                  data-testid="fi-nr-external-checkbox"
                />
                <Label>External numbering (manually supplied)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="fi-nr-save-btn">{editMode ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`FI Number Range ${viewItem?.number_range_id || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default FINumberRanges;
